'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { ArrowLeft } from 'lucide-react';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, doc, getDoc, getDocs } from '@/lib/firestoreTracked';
import { auth } from '@/firebaseConfig';
import { db } from '@/firebaseConfig';
import useCardDesigns from '@/hooks/useCardDesigns';
import { getIconByName } from '@/utils/iconMap';
import { isAndroidTwaLikeRuntime, subscribeToAndroidAppPreview } from '@/lib/runtimePlatform';
import {
  cancelAndroidPlayPurchase,
  getAndroidPlayProductId,
  listAndroidPlayPurchases,
  prepareAndroidPlayPurchase,
  queryAndroidPlayProducts,
  startAndroidPlayPurchase,
} from '@/lib/androidPlayBillingClient';
import SiteFooter from '@/components/SiteFooter';

const SUPPORTER_PLAN_RANK = { '': 0, bronze: 1, silver: 2, gold: 3 };
const LEGACY_TIER_TO_PLAN = { 3: 'bronze', 5: 'silver', 10: 'gold' };
const SUPPORTER_PERKS = [
  {
    key: 'bronze',
    label: 'Bronze',
    titles: [
      'Supporter',
      'Backer',
      'VIP',
      'I♥AV',
      'Acro Ally',
      'Star',
      'Bronze',
      'Bronze Supporter',
      'Bronze Backer',
      'Bronze Beauty',
      'Gleaming',
      'Statuesque',
    ],
    icons: ['Heart', 'Bronze Heart'],
    backgrounds: ['Basic Bronze', 'Cosmic Bronze', 'Metallic Bronze'],
  },
  {
    key: 'silver',
    label: 'Silver',
    titles: [
      'Includes all Bronze titles',
      'Silver',
      'Silver Supporter',
      'Silver Backer',
      'Silverback',
      'Silver Surfer',
      'Stellar',
      'Acroing since the 90s',
      'Acroverse GMV',
      'I Remember Acrophobia',
    ],
    icons: ['Heart', 'Bronze Heart', 'Silver Heart'],
    backgrounds: ['Basic Silver', 'Cosmic Silver', 'Metallic Silver'],
  },
  {
    key: 'gold',
    label: 'Gold',
    titles: [
      'Includes all Silver titles',
      'Gold',
      'Gold Supporter',
      'Gold Backer',
      'Golden',
      'Pure Gold',
      'Priceless',
      'Nugget',
      "Glowin'",
      'Legend',
    ],
    icons: ['Heart', 'Bronze Heart', 'Silver Heart', 'Gold Heart'],
    backgrounds: ['Basic Gold', 'Cosmic Gold', 'Metallic Gold'],
  },
];

const INCLUDES_TITLES_RE = /^Includes all (.+) titles$/i;

export default function SupportPage() {
  const [user, setUser] = useState(null);
  const [loadingKey, setLoadingKey] = useState('');
  const [error, setError] = useState('');
  const [isUSVisitor, setIsUSVisitor] = useState(false);
  const [categoryOverrides, setCategoryOverrides] = useState([]);
  const [supporterPlanRank, setSupporterPlanRank] = useState(0);
  const [isProSubscriber, setIsProSubscriber] = useState(false);
  const [canManageProSubscription, setCanManageProSubscription] = useState(false);
  const [isAndroidWrappedApp, setIsAndroidWrappedApp] = useState(false);
  const [androidPlayProducts, setAndroidPlayProducts] = useState({});
  const [androidPlayProductsStatus, setAndroidPlayProductsStatus] = useState('idle');
  const { cardDesigns, loading: proBackgroundsLoading } = useCardDesigns({ enabled: true });

  useEffect(() => onAuthStateChanged(auth, (nextUser) => setUser(nextUser || null)), []);

  useEffect(() => {
    document.body.classList.add('body-auth-home');
    return () => {
      document.body.classList.remove('body-auth-home');
    };
  }, []);

  useEffect(() => {
    setIsAndroidWrappedApp(isAndroidTwaLikeRuntime());
  }, []);

  useEffect(() => subscribeToAndroidAppPreview(() => {
    setIsAndroidWrappedApp(isAndroidTwaLikeRuntime());
  }), []);

  useEffect(() => {
    if (!isAndroidWrappedApp) {
      setAndroidPlayProducts({});
      setAndroidPlayProductsStatus('idle');
      return undefined;
    }
    let cancelled = false;
    const loadPlayProductDetails = async () => {
      setAndroidPlayProductsStatus('loading');
      try {
        const productIds = [
          getAndroidPlayProductId('supporter', 'bronze'),
          getAndroidPlayProductId('supporter', 'silver'),
          getAndroidPlayProductId('supporter', 'gold'),
          getAndroidPlayProductId('pro'),
        ];
        const details = await queryAndroidPlayProducts(productIds);
        if (cancelled) return;
        if (!Array.isArray(details) || details.length === 0) {
          console.warn('Google Play Billing returned no product details for support products.', { productIds, details });
        }
        setAndroidPlayProducts(
          (Array.isArray(details) ? details : []).reduce((acc, item) => {
            const itemId = String(item?.itemId || item?.sku || item?.productId || '').trim();
            if (itemId) acc[itemId] = item;
            return acc;
          }, {})
        );
        setAndroidPlayProductsStatus('ready');
      } catch (playProductsError) {
        console.warn('Failed to load Google Play Billing product details.', playProductsError);
        if (!cancelled) {
          setAndroidPlayProducts({});
          setAndroidPlayProductsStatus('unavailable');
        }
      }
    };
    loadPlayProductDetails();
    return () => {
      cancelled = true;
    };
  }, [isAndroidWrappedApp]);

  useEffect(() => {
    if (!isAndroidWrappedApp || !user?.uid) return undefined;
    let cancelled = false;
    const syncExistingPlayPurchases = async () => {
      try {
        const purchases = await listAndroidPlayPurchases();
        if (!Array.isArray(purchases) || purchases.length === 0) return;
        const token = await user.getIdToken();
        for (const purchase of purchases) {
          if (cancelled) return;
          const productId = String(purchase?.itemId || purchase?.sku || '').trim();
          const purchaseToken = String(purchase?.purchaseToken || '').trim();
          if (!productId || !purchaseToken) continue;
          const response = await fetch('/api/payments/google-play/acknowledge', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ productId, purchaseToken }),
          }).catch(() => null);
          const data = response?.ok ? await response.json().catch(() => ({})) : {};
          if (data?.mode === 'supporter' && data?.supporterPlan) {
            setSupporterPlanRank((current) => Math.max(current, SUPPORTER_PLAN_RANK[data.supporterPlan] || 0));
          } else if (data?.mode === 'pro') {
            setIsProSubscriber(Boolean(data?.active));
            setCanManageProSubscription(true);
          }
        }
      } catch {
        // Existing purchase recovery is best-effort; explicit checkout still reports errors.
      }
    };
    syncExistingPlayPurchases();
    return () => {
      cancelled = true;
    };
  }, [isAndroidWrappedApp, user]);

  useEffect(() => {
    let cancelled = false;
    const detectGeoPricing = async () => {
      try {
        const response = await fetch('/api/geo', {
          method: 'GET',
          cache: 'no-store',
        });
        if (!response.ok) throw new Error('Geo detection request failed');
        const data = await response.json().catch(() => ({}));
        const country = String(data?.country || '').trim().toUpperCase();
        if (country) {
          if (!cancelled) setIsUSVisitor(country === 'US');
          return;
        }
      } catch {
        // Fall through to locale/timezone fallback.
      }

      if (typeof window === 'undefined') return;
      const localeCandidates = [
        ...(Array.isArray(window.navigator?.languages) ? window.navigator.languages : []),
        window.navigator?.language,
        Intl.DateTimeFormat().resolvedOptions().locale,
      ]
        .map((value) => String(value || '').trim())
        .filter(Boolean);

      const hasUSLocale = localeCandidates.some((tag) => {
        try {
          if (typeof Intl?.Locale === 'function') {
            const locale = new Intl.Locale(tag);
            if (String(locale.region || '').toUpperCase() === 'US') return true;
          }
        } catch {
          // Ignore malformed locale tags.
        }
        return /(?:^|[-_])US(?:$|[-_])/i.test(tag);
      });

      const timeZone = String(Intl.DateTimeFormat().resolvedOptions().timeZone || '');
      const isAmericasTimeZone = /^America\//i.test(timeZone);
      if (!cancelled) setIsUSVisitor(hasUSLocale && isAmericasTimeZone);
    };

    detectGeoPricing();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchCategories = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'cardCategories'));
        const categories = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));
        if (isMounted) {
          setCategoryOverrides(categories);
        }
      } catch (fetchError) {
        console.warn('Failed to load card categories for supporter page', fetchError);
      }
    };
    fetchCategories();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadEntitlements = async () => {
      if (!user?.uid) {
        setSupporterPlanRank(0);
        setIsProSubscriber(false);
        setCanManageProSubscription(false);
        return;
      }
      try {
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        const userData = userSnap.exists() ? userSnap.data() : {};
        const namedPlan = String(userData?.supporter?.plan || '').trim().toLowerCase();
        const normalizedPlan = Object.prototype.hasOwnProperty.call(SUPPORTER_PLAN_RANK, namedPlan)
          ? namedPlan
          : (LEGACY_TIER_TO_PLAN[Number(userData?.supporter?.tier || 0)] || '');
        if (!cancelled) {
          setSupporterPlanRank(SUPPORTER_PLAN_RANK[normalizedPlan] || 0);
          setIsProSubscriber(userData?.pro?.active === true);
          setCanManageProSubscription(Boolean(String(userData?.stripeCustomerId || '').trim() || String(userData?.pro?.source || '') === 'google_play'));
        }
      } catch {
        if (!cancelled) {
          setSupporterPlanRank(0);
          setIsProSubscriber(false);
          setCanManageProSubscription(false);
        }
      }
    };
    loadEntitlements();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const proBackgroundCards = useMemo(() => {
    const categoryById = new Map(categoryOverrides.map((item) => [String(item?.id || ''), item]));
    const proWordPattern = /(?:^|[^a-z])pro(?:$|[^a-z])/i;
    const getCardCategory = (card) => (
      String(
        card?.category
        || card?.categoryId
        || card?.cardCategory
        || card?.css?.category
        || 'basic'
      )
    );
    const isCategoryMarkedProOnly = (category) => {
      if (!category) return false;
      if (Boolean(category.proOnly)) return true;
      const label = String(category.label || '');
      const id = String(category.id || '');
      return proWordPattern.test(label) || proWordPattern.test(id);
    };
    const proTagPattern = /\(\s*pro\s*\)/i;
    const isDeveloperCategoryId = (value) => String(value || '').toLowerCase() === 'developer';

    return cardDesigns
      .filter((card) => !isDeveloperCategoryId(getCardCategory(card)))
      .filter((card) => {
        const categoryId = getCardCategory(card);
        const category = categoryById.get(categoryId);
        const categoryMarkedPro = isCategoryMarkedProOnly(category);
        const name = String(card?.name || '');
        const id = String(card?.id || '');
        return categoryMarkedPro || proTagPattern.test(name) || proTagPattern.test(id);
      })
      .sort((a, b) => String(a?.name || a?.id || '').localeCompare(String(b?.name || b?.id || '')));
  }, [cardDesigns, categoryOverrides]);

  const supporterBackgroundCardsByTier = useMemo(() => {
    const byName = new Map(
      cardDesigns.map((card) => [String(card?.name || '').trim().toLowerCase(), card])
    );
    return SUPPORTER_PERKS.reduce((acc, tier) => {
      acc[tier.key] = (tier.backgrounds || [])
        .map((backgroundName) => ({
          name: backgroundName,
          card: byName.get(String(backgroundName || '').trim().toLowerCase()) || null,
        }));
      return acc;
    }, {});
  }, [cardDesigns]);

  const getProBackgroundStyle = useCallback((card) => {
    const css = card?.css || {};
    const parseNumber = (value, fallback) => {
      if (typeof value === 'number' && Number.isFinite(value)) return value;
      if (typeof value === 'string') {
        const parsed = parseFloat(value);
        if (Number.isFinite(parsed)) return parsed;
      }
      return fallback;
    };
    const backgroundColor = String(css.backgroundColor || 'rgba(15, 23, 42, 0.72)');
    const bgImage = String(css.bgImage || css.backgroundImage || '').trim();
    const bgOpacity = parseNumber(css.bgOpacity ?? css.backgroundOpacity, 1);
    const bgImageOpacity = parseNumber(css.bgImageOpacity ?? css.backgroundImageOpacity, 1);
    const borderColor = String(css.borderColor || 'rgba(148, 163, 184, 0.3)');
    const borderWidth = parseNumber(css.borderWidth, 1);
    const isBorderGradient = borderColor.includes('gradient');
    const textColor = String(css.textColor || '#ffffff');
    const textShadow = String(css.textShadow || 'none');
    const boxShadow = String(css.boxShadow || 'none');

    return {
      background: 'transparent',
      border: 'none',
      '--card-bg': backgroundColor,
      '--card-bg-opacity': bgOpacity,
      '--card-bg-image': bgImage ? `url(${bgImage})` : 'none',
      '--card-bg-image-opacity': bgImageOpacity,
      '--card-bg-size': bgImage ? 'cover' : 'auto',
      '--card-bg-position': bgImage ? 'center center' : 'initial',
      '--card-bg-repeat': bgImage ? 'no-repeat' : 'repeat',
      '--card-border-width': `${borderWidth}px`,
      '--card-border-color': borderColor,
      ...(isBorderGradient ? { '--card-border-gradient': borderColor } : {}),
      '--card-border-radius': '5px',
      '--card-text-color': textColor,
      '--card-text-shadow': textShadow,
      '--card-glow': boxShadow,
      boxShadow: 'none'
    };
  }, []);

  const startAndroidCheckout = useCallback(async (mode, supporterPlan = null) => {
    if (!user) {
      setError('Please sign in before purchasing.');
      return;
    }
    const productId = getAndroidPlayProductId(mode, supporterPlan);
    if (!productId) {
      setError('Google Play product is not configured.');
      return;
    }

    setError('');
    const key = `${mode}:${supporterPlan || 'pro'}`;
    setLoadingKey(key);
    let paymentResponse = null;
    let authToken = '';
    let preparedAttemptId = '';
    try {
      authToken = await user.getIdToken();
      const prepared = await prepareAndroidPlayPurchase({ productId, authToken });
      preparedAttemptId = prepared.attemptId;
      const purchase = await startAndroidPlayPurchase({
        productId,
        obfuscatedAccountId: prepared.accountId,
      });
      paymentResponse = purchase.paymentResponse;
      const response = await fetch('/api/payments/google-play/acknowledge', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          productId: purchase.productId,
          purchaseToken: purchase.purchaseToken,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.ok) {
        throw new Error(data?.error || 'Google Play purchase could not be verified');
      }
      await paymentResponse.complete('success').catch(() => {});
      if (mode === 'supporter') {
        setSupporterPlanRank((current) => Math.max(current, SUPPORTER_PLAN_RANK[supporterPlan] || 0));
      } else {
        setIsProSubscriber(Boolean(data?.active ?? true));
        setCanManageProSubscription(false);
      }
    } catch (checkoutError) {
      if (paymentResponse) await paymentResponse.complete('fail').catch(() => {});

      // If Payment Request never returned a purchase, this checkout attempt cannot
      // be verified. Close the prepared intent for every pre-purchase failure, not
      // just AbortError: Android/Chrome can surface user cancellation as other
      // DOMException/error types depending on the billing bridge/version.
      if (!paymentResponse && preparedAttemptId && authToken) {
        await cancelAndroidPlayPurchase({
          productId,
          attemptId: preparedAttemptId,
          authToken,
        }).catch((cancelError) => {
          console.warn('Failed to close unsuccessful Google Play checkout.', cancelError);
        });
      }

      if (checkoutError?.name === 'AbortError') {
        setError('');
      } else {
        setError(checkoutError?.message || 'Google Play checkout failed');
      }
    } finally {
      setLoadingKey('');
    }
  }, [user]);

  const startCheckout = useCallback(async (mode, supporterPlan = null) => {
    if (isAndroidWrappedApp) {
      await startAndroidCheckout(mode, supporterPlan);
      return;
    }
    if (!user) {
      setError('Please sign in before purchasing.');
      return;
    }
    setError('');
    const key = `${mode}:${supporterPlan || 'pro'}`;
    setLoadingKey(key);
    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/payments/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ mode, supporterPlan }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.url) {
        throw new Error(data?.error || 'Failed to create checkout session');
      }
      window.location.assign(data.url);
    } catch (checkoutError) {
      setError(checkoutError.message || 'Failed to start checkout');
    } finally {
      setLoadingKey('');
    }
  }, [isAndroidWrappedApp, startAndroidCheckout, user]);

  const handleManageSubscription = useCallback(async () => {
    if (isAndroidWrappedApp) {
      window.location.assign('https://play.google.com/store/account/subscriptions');
      return;
    }
    if (!user) {
      setError('Sign in first to manage your subscription.');
      return;
    }

    setError('');
    setLoadingKey('pro:manage');
    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/payments/portal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.url) {
        throw new Error(data?.error || 'Failed to open subscription management');
      }
      window.location.assign(data.url);
    } catch (manageError) {
      setError(manageError.message || 'Failed to open subscription management');
    } finally {
      setLoadingKey('');
    }
  }, [isAndroidWrappedApp, user]);

  const getAndroidPlayPriceLabel = useCallback((mode, supporterPlan, fallback) => {
    if (!isAndroidWrappedApp) return fallback;
    const productId = getAndroidPlayProductId(mode, supporterPlan);
    const item = androidPlayProducts[productId];
    const externalLocalisedPriceKey = ['local', 'izedPrice'].join('');
    const directFormattedPrice = String(
      item?.formattedPrice
      || item?.priceFormatted
      || item?.localisedPrice
      || item?.[externalLocalisedPriceKey]
      || ''
    ).trim();
    if (directFormattedPrice) {
      if (mode === 'pro' && !/\/\s*(month|mo|mth)|month/i.test(directFormattedPrice)) {
        return `${directFormattedPrice}/month`;
      }
      return directFormattedPrice;
    }

    const amount = item?.price;
    if (typeof amount === 'string') {
      const price = amount.trim();
      if (price) return mode === 'pro' && !/\/\s*(month|mo|mth)|month/i.test(price) ? `${price}/month` : price;
      return null;
    }
    if (!amount?.currency || amount?.value === undefined || amount?.value === null) return null;
    const numericValue = Number(amount.value);
    const currency = String(amount.currency || '').trim().toUpperCase();
    if (!currency) return null;
    if (!Number.isFinite(numericValue)) return null;
    try {
      const price = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
      }).format(numericValue);
      if (mode === 'pro') return `${price}/month`;
      return price;
    } catch {
      return null;
    }
  }, [androidPlayProducts, isAndroidWrappedApp]);

  const getAndroidPlayPriceFallback = useCallback((fallback) => {
    if (!isAndroidWrappedApp) return fallback;
    if (androidPlayProductsStatus === 'loading') return 'Checking price';
    return 'Play price';
  }, [androidPlayProductsStatus, isAndroidWrappedApp]);

  return (
    <div className="supportPageShell">
      <main className="supportPage">
        <div className="supportPage__layout">
          <section className="supportPage__card marketingFaqItem">
            <h1 className="supportPage__title">Support Acroverse</h1>
            <p className="supportPage__intro">
           Hi, I’m Ben — you’ll see me around as Konador. I built Acroverse as a solo passion project, inspired by my love of word games and the humour they can create.
</p><p className="supportPage__intro">Keeping an online game fast and reliable isn’t free, so if you’ve enjoyed playing and want to help shape what comes next, this is how you can do that.
Supporters unlock <b>exclusive lifetime perks</b> instantly with a one-off donation, or you can go Pro for full access to <b>premium customisation options</b>.
</p><p className="supportPage__intro">
Supporter perks include <b>more ways to earn XP in the app</b>, including the Android Daily Set Bonus XP.
</p><p className="supportPage__intro">
Your support keeps Acroverse fast, free, and constantly evolving. <b>Thank you!</b></p>
            {!user && <p className="supportPage__error">You are not signed in. Sign in first to purchase.</p>}

            <h2 className="supportPage__sectionTitle">Supporter (one-off)</h2>
            <div className="supportPage__optionGrid">
              {(() => {
                const planPrices = isUSVisitor
                  ? { bronze: '$4', silver: '$7', gold: '$12' }
                  : { bronze: '£3', silver: '£5', gold: '£10' };
                const planMeta = {
                  bronze: 'Unlock it. Keep it.',
                  silver: 'Permanent. No strings.',
                  gold: 'Lifetime flex.'
                };
                return ['bronze', 'silver', 'gold'].map((plan) => {
                  const key = `supporter:${plan}`;
                  const planLabel = `${plan.charAt(0).toUpperCase()}${plan.slice(1)}`;
                  const priceLabel = getAndroidPlayPriceLabel('supporter', plan, planPrices[plan] || '')
                    || getAndroidPlayPriceFallback(planPrices[plan] || '');
                  const tierRank = SUPPORTER_PLAN_RANK[plan] || 0;
                  const isOwnedTier = supporterPlanRank >= tierRank;
                  return (
                    <article key={plan} className={`supportPage__optionCard supportPage__optionCard--${plan}`}>
                      <div className="supportPage__proMeta">
                        <h3 className={`supportPage__planName supportPage__planName--${plan}`}>{planLabel}</h3>
                        <span>{priceLabel}</span>
                      </div>
                      <p className="supportPage__planMeta">{planMeta[plan] || 'Unlock it. Keep it forever.'}</p>
                      <button
                        type="button"
                        className="button buttonPrimary supportPage__cta"
                        onClick={() => startCheckout('supporter', plan)}
                        disabled={!user || loadingKey === key || isOwnedTier}
                      >
                        {loadingKey === key ? (isAndroidWrappedApp ? 'Opening Play...' : 'Redirecting...') : isOwnedTier ? 'THANK YOU' : `Unlock ${planLabel}`}
                      </button>
                    </article>
                  );
                });
              })()}
            </div>

            <h2 className="supportPage__sectionTitle">Pro subscription</h2>
            <article className="supportPage__proCard supportPage__proCard--pro">
              <div className="supportPage__proMeta">
                <strong>Pro</strong>
                <span>{getAndroidPlayPriceLabel('pro', null, isUSVisitor ? '$5/month' : '£4/month') || getAndroidPlayPriceFallback(isUSVisitor ? '$5/month' : '£4/month')}</span>
              </div>
              <p className="supportPage__planMeta">Unlock every supporter perk, every premium background (including particle effects!), and all future Pro customisation features.</p>
              {isProSubscriber ? (
                <div className="supportPage__ctaRow">
                  <button
                    type="button"
                    className="button buttonPrimary supportPage__cta"
                    disabled
                  >
                    PRO SUBSCRIPTION ACTIVE
                  </button>
                  {canManageProSubscription && (
                    <button
                      type="button"
                      className="button supportPage__cta supportPage__cta--secondary supportPage__cta--manage"
                      onClick={handleManageSubscription}
                      disabled={!user || loadingKey === 'pro:manage'}
                    >
                      {loadingKey === 'pro:manage' ? 'Opening...' : 'Manage'}
                    </button>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  className="button buttonPrimary supportPage__cta"
                  onClick={() => startCheckout('pro')}
                  disabled={!user || loadingKey === 'pro:pro'}
                >
                  {loadingKey === 'pro:pro' ? (isAndroidWrappedApp ? 'Opening Play...' : 'Redirecting...') : 'Activate Pro'}
                </button>
              )}
              <p className="supportPage__note">
                Secure checkout via {isAndroidWrappedApp ? 'Google Play Billing' : 'Stripe'}. Unlocks are applied automatically after payment. Some cosmetic card designs may incorporate AI-assisted artwork, curated and refined for Acroverse.
              </p>
            </article>
            <section className="supportPage__proBackgrounds" aria-label="Pro card backgrounds">
              <h3 className="supportPage__sectionTitle">Pro Playercard Backgrounds</h3>
              {proBackgroundsLoading ? (
                <p className="supportPage__planMeta">Loading Pro backgrounds...</p>
              ) : (
                <ul className="supportPage__proBackgroundGrid">
                  {proBackgroundCards.length > 0 ? (
                    proBackgroundCards.map((card) => (
                      <li key={`pro-bg-${card.id}`} className="supportPage__proBackgroundPill">
                        <div className="supportPage__proBackgroundCard player-card-custom" style={getProBackgroundStyle(card)}>
                          <div className="player-card-background" aria-hidden="true" />
                          <div className="player-card-glow" aria-hidden="true" />
                        </div>
                      </li>
                    ))
                  ) : (
                    <li className="supportPage__proBackgroundPill supportPage__proBackgroundPill--muted">
                      Pro card backgrounds coming soon
                    </li>
                  )}
                </ul>
              )}
            </section>

            {error && <p className="supportPage__error">{error}</p>}
            <div className="supportPage__divider" aria-hidden="true" />
            <Link href="/" className="button supportPage__backButton">
              <ArrowLeft size={16} strokeWidth={2} />
              <span>Back to game</span>
            </Link>
          </section>

          <aside className="supportPage__unlocksCard" aria-label="Supporter perks by tier">
            <div className="supportPage__unlocksList">
              {SUPPORTER_PERKS.map((tier) => {
                const includesText = tier.titles.find((item) => INCLUDES_TITLES_RE.test(item)) || '';
                const includesMatch = includesText.match(INCLUDES_TITLES_RE);
                const includesTier = includesMatch?.[1] || '';
                const titlePerks = tier.titles.filter((item) => !INCLUDES_TITLES_RE.test(item));
                const iconPerks = tier.icons || [];
                const tierRank = SUPPORTER_PLAN_RANK[tier.key] || 0;
                return (
                  <section key={tier.key} className={`supportPage__unlockGroup supportPage__unlockGroup--${tier.key} marketingFaqItem`}>
                    {!!includesTier && (
                      <span className="supportPage__unlockIncludes">Includes {includesTier}</span>
                    )}
                    <h3 className={`supportPage__unlockHeading supportPage__unlockHeading--${tier.key}`}>{tier.label}</h3>

                    <h4 className="supportPage__unlockSubhead">Titles</h4>
                    <ul className="supportPage__perkGrid" aria-label={`${tier.label} title perks`}>
                      {titlePerks.map((item) => (
                        <li key={`${tier.key}-titles-${item}`} className="supportPage__perkPill">{item}</li>
                      ))}
                    </ul>

                    <h4 className="supportPage__unlockSubhead">Icons</h4>
                    <ul className="supportPage__perkGrid" aria-label={`${tier.label} icon perks`}>
                      {iconPerks.map((iconName) => {
                        const iconKey = iconName === 'Heart'
                          ? 'SupporterHeart'
                          : iconName === 'Bronze Heart'
                            ? 'SupporterHeartBronze'
                            : iconName === 'Silver Heart'
                              ? 'SupporterHeartSilver'
                              : 'SupporterHeartGold';
                        const IconComponent = getIconByName(iconKey);
                        return (
                          <li
                            key={`${tier.key}-icon-${iconName}`}
                            className="supportPage__perkPill"
                            title={tier.label}
                          >
                            {IconComponent ? (
                              <span className={`supportPage__perkIcon supportPage__perkIcon--${tier.key}`} aria-hidden="true">
                                <IconComponent size={14} strokeWidth={1.8} />
                              </span>
                            ) : null}
                            <span>{iconName}</span>
                          </li>
                        );
                      })}
                    </ul>

                    <h4 className="supportPage__unlockSubhead">Backgrounds</h4>
                    <ul className="supportPage__supporterBackgroundGrid" aria-label={`${tier.label} background perks`}>
                      {(supporterBackgroundCardsByTier[tier.key] || []).map(({ name: backgroundName, card }) => {
                        return (
                          <li
                            key={`${tier.key}-bg-${backgroundName}`}
                            className="supportPage__supporterBackgroundPill"
                            title={tier.label}
                          >
                            <div className={`supportPage__supporterBackgroundCard player-card-custom ${!card ? 'supportPage__supporterBackgroundCard--missing' : ''}`} style={card ? getProBackgroundStyle(card) : undefined}>
                              <div className="player-card-background" aria-hidden="true" />
                              <div className="player-card-glow" aria-hidden="true" />
                              <span className="supportPage__supporterBackgroundLabel">{backgroundName}</span>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                );
              })}
            </div>
          </aside>
        </div>
      </main>

      <SiteFooter hideSupportLink />
    </div>
  );
}
